"use client";

import { useTranslations } from "next-intl";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { useVotingPeriod } from "@/hooks/dao/useVotingPeriod";
import { CycleCountdown } from "./CycleCountdown";

/**
 * Top banner of the election tab — single CycleCountdown card per Figma 5462:51502 (neutral)
 * + Figma 5462:51807 (active yellow border).
 *
 * No title text, no extra wrapper. CycleCountdown owns full visual layout.
 */
export const CycleBanner: React.FC = () => {
  const t = useTranslations("dao");
  const tCommon = useTranslations("common");
  const { cycle } = useCurrentCycle();
  const {
    isVotingPeriod,
    voteEndUnix,
    nextCycleStartUnix,
    isLoading,
    isError,
    refetch,
  } = useVotingPeriod(cycle);

  if (isLoading) {
    return <Skeleton className="h-16 w-full rounded-2xl" />;
  }

  if (isError) {
    return (
      <div
        dir="rtl"
        className="flex w-full items-center justify-between gap-3 rounded-2xl bg-bg-secondary p-3"
      >
        <p className="text-sm text-semantic-error">
          {t("election.cycle.loadError")}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-sm text-accent hover:underline"
          aria-label={t("election.cycle.retryAria")}
        >
          {tCommon("retry")}
        </button>
      </div>
    );
  }

  if (isVotingPeriod && voteEndUnix) {
    return <CycleCountdown variant="active" targetUnixSeconds={voteEndUnix} />;
  }

  return (
    <CycleCountdown
      variant="neutral"
      targetUnixSeconds={nextCycleStartUnix ?? 0}
    />
  );
};
